import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Skeleton,
  SvgIconProps,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LogoutIcon from "@mui/icons-material/Logout";
import ScheduleIcon from "@mui/icons-material/Schedule";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useTranslation } from "react-i18next";
import { useSessionStatus } from "@/hooks/useTroubleshooting";
import { SessionState } from "@/domain/troubleshooting.interface";
import { formatInBackofficeLondon } from "@/utils/dateUtils";
import { TIMELINE_TIME_FORMAT } from "./TimelineSwimlane";

/** Badge per session state: colour plus a distinct icon (not colour alone). */
export const SESSION_STATE_META: Record<
  SessionState,
  {
    color: "success" | "error" | "warning" | "default";
    Icon: React.ComponentType<SvgIconProps>;
  }
> = {
  ACTIVE: { color: "success", Icon: CheckCircleIcon },
  ENDED: { color: "error", Icon: LogoutIcon },
  STALE: { color: "warning", Icon: ScheduleIcon },
  UNKNOWN: { color: "default", Icon: HelpOutlineIcon },
};

const fmt = (iso?: string) =>
  iso ? formatInBackofficeLondon(iso, TIMELINE_TIME_FORMAT) : "-";

const Item: React.FC<{
  label: string;
  children: React.ReactNode;
  testId?: string;
}> = ({ label, children, testId }) => (
  <Box sx={{ minWidth: 200 }} data-testid={testId}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ fontWeight: 600, display: "block" }}
    >
      {label}
    </Typography>
    <Typography variant="body2" component="div">
      {children}
    </Typography>
  </Box>
);

/**
 * COYN-1507 §6.1: what the backend can infer about the app's session right
 * now (the app itself cannot report while it has no session).
 */
const SessionStatusCard: React.FC<{
  participantId: string;
  /** Live mode: refresh periodically (the geofence sync card shares it). */
  live?: boolean;
  /** Live refresh period (from the participantTimeline setting). */
  liveIntervalMs?: number;
}> = ({ participantId, live = false, liveIntervalMs }) => {
  const { t } = useTranslation("troubleshooting");
  const tt = t as (key: string, options?: Record<string, unknown>) => string;
  const {
    data: status,
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useSessionStatus(participantId, { live, liveIntervalMs });

  const meta = status
    ? SESSION_STATE_META[status.state] || SESSION_STATE_META.UNKNOWN
    : null;
  const pushes = status?.pushesSinceLastSeen;

  return (
    <Card
      variant="outlined"
      sx={{ height: "100%" }}
      data-testid="session-status-card"
    >
      <CardContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {t("session.title")}
            </Typography>
            {status && meta && (
              <Chip
                color={meta.color}
                icon={<meta.Icon aria-hidden />}
                label={tt(`session.state.${status.state}`, {
                  defaultValue: status.state,
                })}
                data-testid="session-state-badge"
                data-state={status.state}
                sx={{ fontWeight: 700 }}
              />
            )}
            {status?.reasonCode && (
              <Chip size="small" variant="outlined" label={status.reasonCode} />
            )}
          </Box>
          <Tooltip title={t("session.refresh")}>
            <span>
              <IconButton
                size="small"
                aria-label={t("session.refresh")}
                onClick={() => refetch()}
                disabled={isFetching}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {isLoading && (
          <Box aria-label={t("session.loading")}>
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="80%" />
          </Box>
        )}

        {isError && !status && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => refetch()}>
                {t("timeline.retry")}
              </Button>
            }
          >
            {t("session.error")}
          </Alert>
        )}

        {status && (
          <>
            <Typography
              variant="body1"
              sx={{ mb: 1.5 }}
              data-testid="session-reason"
            >
              {status.reason || t("session.noReason")}
            </Typography>

            {status.sdkAliveWithoutSession && (
              <Alert
                severity="warning"
                sx={{ mb: 1.5 }}
                data-testid="session-sdk-alive-warning"
              >
                {t("session.sdkAliveWithoutSession")}
              </Alert>
            )}

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              <Item label={t("session.fields.lastSeen")}>
                {fmt(status.lastSeenAt)}
              </Item>
              <Item label={t("session.fields.lastSignIn")}>
                {fmt(status.lastSignInAt)}
              </Item>
              <Item label={t("session.fields.lastRefreshOk")}>
                {fmt(status.lastRefreshOkAt)}
              </Item>
              <Item
                label={t("session.fields.lastRefreshRejected")}
                testId="session-refresh-rejected"
              >
                {fmt(status.lastRefreshRejectedAt)}
                {status.lastRefreshRejectedCode && (
                  <Box component="span" sx={{ fontFamily: "monospace" }}>
                    {` (${status.lastRefreshRejectedCode})`}
                  </Box>
                )}
              </Item>
              <Item
                label={t("session.fields.sessionEnded")}
                testId="session-ended"
              >
                {fmt(status.lastSessionEndAt)}
                {status.lastSessionEndReason && (
                  <Box component="span" sx={{ fontFamily: "monospace" }}>
                    {` (${status.lastSessionEndReason})`}
                  </Box>
                )}
              </Item>
              <Item
                label={t("session.fields.lastCrash")}
                testId="session-crash"
              >
                {fmt(status.lastCrashAt)}
                {status.lastCrashKind && (
                  <Box component="span" sx={{ fontFamily: "monospace" }}>
                    {` (${status.lastCrashKind})`}
                  </Box>
                )}
              </Item>
              <Item label={t("session.fields.lastGeofenceEvent")}>
                {fmt(status.lastGeofenceEventAt)}
              </Item>
              <Item label={t("session.fields.app")}>
                {[status.appVersion, status.osType]
                  .filter(Boolean)
                  .join(" · ") || "-"}
              </Item>
              <Item
                label={t("session.fields.estimatedExpiry")}
                testId="session-estimated-expiry"
              >
                <Tooltip title={t("session.estimatedHint")}>
                  <span>{fmt(status.estimatedSessionExpiresAt)}</span>
                </Tooltip>
              </Item>
              {pushes && (
                <Item
                  label={t("session.pushesSinceLastSeen")}
                  testId="session-pushes"
                >
                  {tt("session.pushes", {
                    sent: pushes.sent,
                    skipped: pushes.skipped,
                    clicked: pushes.clicked,
                  })}
                </Item>
              )}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default SessionStatusCard;
